import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Skeleton,
  Alert,
  alpha,
  IconButton,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Person as PersonIcon,
  LocalHospital as HospitalIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  CalendarMonth as CalendarIcon,
  Schedule as ScheduleIcon,
  MedicalServices as MedicalIcon,
  CheckCircle as CheckIcon,
  HourglassEmpty as PendingIcon,
  Cancel as CancelIcon,
  Star as StarIcon,
  AccessTime as TimeIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Badge as BadgeIcon,
  Description as ReportIcon,
  TrendingUp as TrendIcon,
  EventAvailable as AvailableIcon,
  EventBusy as LeaveIcon,
} from '@mui/icons-material';
import axios from 'axios';

// Types
interface DoctorProfile {
  id: number;
  userId: number;
  username: string;
  employeeId: string;
  fullName: string;
  email: string;
  mobile: string;
  specialization: string;
  qualification: string;
  experienceYears: number;
  department: string;
  designation: string;
  status: string;
  registrationNumber: string;
  profilePhotoUrl: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  joiningDate: string;
  createdAt: string;
  // Stats - will be calculated
  todayAppointments?: number;
  completedToday?: number;
  pendingAppointments?: number;
  totalPatients?: number;
  avgRating?: number;
}

interface TodaySchedule {
  id: number;
  bookingNumber: string;
  patientName: string;
  appointmentTime: string;
  status: string;
  priority: string;
  visitType: string;
  address: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box hidden={value !== index} sx={{ pt: 2 }}>
    {value === index && children}
  </Box>
);

const DoctorProfilePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [doctor, setDoctor] = useState<DoctorProfile | null>(null);
  const [schedule, setSchedule] = useState<TodaySchedule[]>([]);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    if (id) {
      fetchDoctorProfile();
      fetchTodaySchedule();
    }
  }, [id]);

  const fetchDoctorProfile = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/doctors/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setDoctor(response.data.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load doctor profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchTodaySchedule = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/appointments/doctor/${id}/today`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSchedule(response.data.data || []);
      }
    } catch (err) {
      // Ignore schedule errors
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      APPROVED: '#10b981',
      PENDING: '#f59e0b',
      REJECTED: '#ef4444',
      SUSPENDED: '#6b7280',
    };
    return colors[status] || '#6b7280';
  };

  const getAppointmentStatusColor = (status: string) => {
    const colors: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
      PENDING: 'warning',
      ACCEPTED: 'info',
      IN_PROGRESS: 'info',
      CLOSED: 'success',
      CANCELLED: 'error',
    };
    return colors[status?.toUpperCase()] || 'default';
  };

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, 'error' | 'warning' | 'info' | 'default'> = {
      EMERGENCY: 'error',
      HIGH: 'warning',
      NORMAL: 'info',
      LOW: 'default',
    };
    return colors[priority?.toUpperCase()] || 'default';
  };

  const formatTime = (dateStr: string) => {
    if (!dateStr) return '--:--';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '--/--/----';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2, borderRadius: 2 }} />
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={4}><Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} /></Grid>
          <Grid item xs={12} md={8}><Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} /></Grid>
        </Grid>
      </Box>
    );
  }

  if (error || !doctor) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Doctor not found'}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: 'transparent' , minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(-1)}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b' }}>
              Doctor Profile
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View and manage doctor information
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<CalendarIcon />}
            onClick={() => navigate(`/doctors/${id}/schedule`)}
          >
            View Schedule
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Profile Card */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
            {/* Header Banner */}
            <Box
              sx={{
                height: 100,
                background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                position: 'relative',
              }}
            >
              <Chip
                label={doctor.status}
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  bgcolor: '#fff',
                  color: getStatusColor(doctor.status),
                  fontWeight: 600,
                }}
              />
            </Box>

            {/* Avatar & Basic Info */}
            <Box sx={{ px: 3, pb: 3, pt: 0, mt: -5 }}>
              <Avatar
                src={doctor.profilePhotoUrl}
                sx={{
                  width: 100,
                  height: 100,
                  border: '4px solid #fff',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  bgcolor: 'transparent' ,
                  fontSize: '2.5rem',
                  fontWeight: 700,
                }}
              >
                {doctor.fullName?.charAt(0)}
              </Avatar>

              <Typography variant="h5" sx={{ fontWeight: 700, mt: 2, color: '#1e293b' }}>
                {doctor.fullName}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {doctor.specialization}
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Chip icon={<WorkIcon />} label={doctor.department} size="small" variant="outlined" />
                <Chip icon={<BadgeIcon />} label={doctor.designation} size="small" variant="outlined" />
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Quick Stats */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 1, bgcolor: alpha('#059669', 0.05), borderRadius: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#059669' }}>
                      {doctor.completedToday || schedule.filter(s => s.status === 'CLOSED').length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Today's Visits</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 1, bgcolor: alpha('#2563eb', 0.05), borderRadius: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#2563eb' }}>
                      {doctor.totalPatients || schedule.length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Total Patients</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 1, bgcolor: alpha('#f59e0b', 0.05), borderRadius: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#f59e0b' }}>
                      {doctor.pendingAppointments || schedule.filter(s => s.status === 'PENDING' || s.status === 'ACCEPTED').length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Pending</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 1, bgcolor: alpha('#8b5cf6', 0.05), borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#8b5cf6' }}>
                        {doctor.avgRating?.toFixed(1) || '4.5'}
                      </Typography>
                      <StarIcon sx={{ color: '#f59e0b', fontSize: 20 }} />
                    </Box>
                    <Typography variant="caption" color="text.secondary">Rating</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>

          {/* Contact Info Card */}
          <Paper sx={{ borderRadius: 2, mt: 2, p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Contact Information
            </Typography>
            <List disablePadding>
              <ListItem sx={{ px: 0, py: 1 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <BadgeIcon sx={{ color: '#64748b' }} />
                </ListItemIcon>
                <ListItemText
                  primary={doctor.employeeId}
                  secondary="Employee ID"
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
              </ListItem>
              <ListItem sx={{ px: 0, py: 1 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <EmailIcon sx={{ color: '#64748b' }} />
                </ListItemIcon>
                <ListItemText
                  primary={doctor.email}
                  secondary="Email"
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
              </ListItem>
              <ListItem sx={{ px: 0, py: 1 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <PhoneIcon sx={{ color: '#64748b' }} />
                </ListItemIcon>
                <ListItemText
                  primary={doctor.mobile || 'Not provided'}
                  secondary="Phone"
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
              </ListItem>
              <ListItem sx={{ px: 0, py: 1 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <LocationIcon sx={{ color: '#64748b' }} />
                </ListItemIcon>
                <ListItemText
                  primary={doctor.address || `${doctor.city}, ${doctor.state}`}
                  secondary="Address"
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Right Column - Details */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ borderRadius: 2 }}>
            {/* Tabs */}
            <Box sx={{ borderBottom: '1px solid #e5e7eb' }}>
              <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
                <Tab label="Professional Info" />
                <Tab label="Today's Schedule" />
                <Tab label="Statistics" />
              </Tabs>
            </Box>

            {/* Professional Info Tab */}
            <TabPanel value={tabValue} index={0}>
              <Box sx={{ p: 2 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <List disablePadding>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon><SchoolIcon color="primary" /></ListItemIcon>
                        <ListItemText
                          primary="Qualification"
                          secondary={doctor.qualification || 'Not specified'}
                          primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                          secondaryTypographyProps={{ fontWeight: 600 }}
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon><MedicalIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Specialization"
                          secondary={doctor.specialization}
                          primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                          secondaryTypographyProps={{ fontWeight: 600 }}
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon><TimeIcon color="info" /></ListItemIcon>
                        <ListItemText
                          primary="Experience"
                          secondary={`${doctor.experienceYears || 0} years`}
                          primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                          secondaryTypographyProps={{ fontWeight: 600 }}
                        />
                      </ListItem>
                    </List>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <List disablePadding>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon><WorkIcon color="warning" /></ListItemIcon>
                        <ListItemText
                          primary="Department"
                          secondary={doctor.department || 'Not assigned'}
                          primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                          secondaryTypographyProps={{ fontWeight: 600 }}
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon><BadgeIcon color="secondary" /></ListItemIcon>
                        <ListItemText
                          primary="Designation"
                          secondary={doctor.designation || 'Not specified'}
                          primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                          secondaryTypographyProps={{ fontWeight: 600 }}
                        />
                      </ListItem>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary="Registration No."
                          secondary={doctor.registrationNumber || 'Not provided'}
                          primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                          secondaryTypographyProps={{ fontWeight: 600 }}
                        />
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Joining Date</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{formatDate(doctor.joiningDate)}</Typography>
                  </Box>
                  <Box sx={{ ml: 4 }}>
                    <Typography variant="caption" color="text.secondary">Registered On</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{formatDate(doctor.createdAt)}</Typography>
                  </Box>
                </Box>
              </Box>
            </TabPanel>

            {/* Today's Schedule Tab */}
            <TabPanel value={tabValue} index={1}>
              <Box sx={{ p: 2 }}>
                {schedule.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Time</TableCell>
                          <TableCell>Booking No.</TableCell>
                          <TableCell>Patient</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Priority</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {schedule.map((appt) => (
                          <TableRow key={appt.id} hover>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {formatTime(appt.appointmentTime)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={appt.bookingNumber} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell>{appt.patientName}</TableCell>
                            <TableCell>
                              <Chip
                                icon={appt.visitType === 'HOME' ? <HospitalIcon /> : <MedicalIcon />}
                                label={appt.visitType}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={appt.priority}
                                size="small"
                                color={getPriorityColor(appt.priority)}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={appt.status}
                                size="small"
                                color={getAppointmentStatusColor(appt.status)}
                              />
                            </TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                onClick={() => navigate(`/appointments/${appt.id}`)}
                              >
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CalendarIcon sx={{ fontSize: 48, color: '#e5e7eb', mb: 1 }} />
                    <Typography color="text.secondary">No appointments scheduled for today</Typography>
                  </Box>
                )}
              </Box>
            </TabPanel>

            {/* Statistics Tab */}
            <TabPanel value={tabValue} index={2}>
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Card sx={{ bgcolor: alpha('#059669', 0.05), borderRadius: 2 }}>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h3" sx={{ fontWeight: 700, color: '#059669' }}>
                          {doctor.completedToday || schedule.filter(s => s.status === 'CLOSED').length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Completed Today</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card sx={{ bgcolor: alpha('#2563eb', 0.05), borderRadius: 2 }}>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h3" sx={{ fontWeight: 700, color: '#2563eb' }}>
                          {doctor.todayAppointments || schedule.length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Total Today</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card sx={{ bgcolor: alpha('#f59e0b', 0.05), borderRadius: 2 }}>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h3" sx={{ fontWeight: 700, color: '#f59e0b' }}>
                          {doctor.pendingAppointments || schedule.filter(s => s.status === 'PENDING' || s.status === 'ACCEPTED').length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Pending</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card sx={{ bgcolor: alpha('#8b5cf6', 0.05), borderRadius: 2 }}>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h3" sx={{ fontWeight: 700, color: '#8b5cf6' }}>
                          {doctor.totalPatients}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Total Patients</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3, mb: 2 }}>
                  Performance Metrics
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">Appointment Completion Rate</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {(doctor.todayAppointments || schedule.length) > 0
                            ? Math.round(((doctor.completedToday || schedule.filter(s => s.status === 'CLOSED').length) / (doctor.todayAppointments || schedule.length)) * 100)
                            : 0}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(doctor.todayAppointments || schedule.length) > 0
                          ? ((doctor.completedToday || schedule.filter(s => s.status === 'CLOSED').length) / (doctor.todayAppointments || schedule.length)) * 100
                          : 0}
                        sx={{ height: 8, borderRadius: 4 }}
                        color="success"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </TabPanel>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DoctorProfilePage;
